import { autocompletion } from "@codemirror/autocomplete";

import { formatTimeWindow } from "src/featureCatalogue/utils";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

const getMinWindowHopFromWindowLength = (windowLength: number) => {
  const ONE_HOUR = 3600;
  const ONE_DAY = 86400;
  const SIXTY_DAYS = 5184000;

  const FIFTEEN_SECONDS = 15;
  const ONE_MINUTE_IN_SECONDS = 60;
  const ONE_HOUR_IN_SECONDS = 3600;
  const ONE_DAY_IN_SECONDS = 86400;

  switch (true) {
    case windowLength < ONE_HOUR:
      return FIFTEEN_SECONDS;
    case windowLength < ONE_DAY:
      return ONE_MINUTE_IN_SECONDS;
    case windowLength < SIXTY_DAYS:
      return ONE_HOUR_IN_SECONDS;
    default:
      return ONE_DAY_IN_SECONDS;
  }
};

export const getWindowHopOptions = () => {
  const isPowerToolsEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.powertools);

  const allOptions = [
    30, 60, 120, 180, 240, 300, 360, 480, 540, 600, 720, 900, 960, 1080, 1200,
    1440, 1800, 1920, 2160, 2400, 2700, 2880, 3600, 4320, 4800, 5400, 5760,
    7200, 8640, 9600, 10800, 14400, 17280, 21600, 28800, 43200, 86400,
  ];

  // Start from 30 if powertools flag is enabled, otherwise start from 300
  const startValue = isPowerToolsEnabled ? 30 : 300;
  const filteredOptions = allOptions.filter((value) => value >= startValue);

  return filteredOptions.map((duration) => ({
    key: duration.toString(),
    value: formatTimeWindow(duration),
  }));
};

export const getWindowLengthOptions = () => {
  const isPowerToolsEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.powertools);

  const allOptions = [
    60, // 1m
    120, // 2m
    240, // 4m
    300, // 5m
    600, // 10m
    1200, // 20m
    1800, // 30m
    3600, // 60m
    5400, // 90m
    7200, // 2h
    10800, // 3h
    14400, // 4h
    21600, // 6h
    43200, // 12h
    86400, // 1d
    172800, // 2d
    345600, // 4d
    604800, // 1w
    1209600, // 14d
    2592000, // 30d
    5184000, // 60d
    7776000, // 90d
    15552000, // 180d
    31536000, // 365d
  ];

  // Start from 300 (5 minutes) if no limits flag is enabled, otherwise allow all options
  const startValue = isPowerToolsEnabled ? 30 : 300;
  const filteredOptions = allOptions.filter((value) => value >= startValue);

  return filteredOptions.map((duration) => ({
    key: duration.toString(),
    value: formatTimeWindow(duration),
  }));
};

export const isWindowHopOptionDisabled = (
  value: number,
  windowLength: number | null,
) =>
  windowLength
    ? value > windowLength ||
      value < getMinWindowHopFromWindowLength(windowLength)
    : false;

const COMPLETION_EXTENSION = autocompletion({
  override: [() => null],
});

export const getCompletionExtension = (isDisabled: boolean) => {
  // If disabled, return empty array to prevent setting up autocompletion
  if (isDisabled) {
    return [];
  }
  return COMPLETION_EXTENSION;
};

export const getWindowLengthSuffix = (seconds: number | undefined): string => {
  if (!seconds) return "";
  const DAY = 86400;
  const HOUR = 3600;
  const MINUTE = 60;

  if (seconds >= DAY && seconds % DAY === 0) return `${seconds / DAY}d`;
  if (seconds >= HOUR && seconds % HOUR === 0) return `${seconds / HOUR}h`;
  if (seconds >= MINUTE && seconds % MINUTE === 0)
    return `${seconds / MINUTE}m`;
  return `${seconds}s`;
};

export const generateFeatureMetadata = (
  columnName: string,
  windowLength: number | undefined,
) => {
  const suffix = getWindowLengthSuffix(windowLength);
  const featureKey = suffix ? `${columnName}_${suffix}` : columnName;
  const featureName =
    columnName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") + (suffix ? ` ${suffix}` : "");

  return {
    featureKey,
    featureName,
  };
};
